import { memo, type Ref, useImperativeHandle, useState } from 'react'

import { useGlobalState } from '@/hooks/useGlobalState'
import type { BulkUploadMode } from '@/utils/bulkContentUpload'

import { BulkUploadWizard } from './BulkUploadWizard'

type Target = { taskId: string; moduleTitle?: string }

/** `target` is the module a supplementary upload adds to, read when the wizard opens. */
export type BulkUploadLauncherHandle = { open: (target?: Target) => void }

/**
 * The bulk upload wizard with its own open state, so opening it doesn't re-render the screen behind
 * it: the library's module table, or Edit CM's file list.
 */
export const BulkUploadLauncher = memo(function BulkUploadLauncher({
  ref,
  mode,
}: {
  ref: Ref<BulkUploadLauncherHandle>
  mode: BulkUploadMode
}) {
  const { bulkUploadStore } = useGlobalState()
  const [opened, setOpened] = useState(false)
  const [target, setTarget] = useState<Target>()
  useImperativeHandle(
    ref,
    () => ({
      open: next => {
        setTarget(next)
        setOpened(true)
      },
    }),
    [],
  )

  return (
    <BulkUploadWizard
      mode={mode}
      opened={opened}
      moduleTitle={target?.moduleTitle}
      onClose={() => setOpened(false)}
      onConfirm={files => bulkUploadStore.start(files, target)}
    />
  )
})
